/**
 * Order Timeline Tab
 *
 * Renders a vertical audit timeline for an order.
 * Fetches events when mounted; shows loading skeleton and empty state.
 * Self-contained — receives only orderId, manages its own fetch lifecycle.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from '@ufc/core'
import { useSelector } from '@ufc/core'
import { cn, Activity, Loader2 } from '@ufc/uikit'
import { selectUserRoleType } from '@ufc/domains/auth'

import type { OrderEvent, OrderEventEntityType } from '../../../domain/types/order-timeline.types'
import { ordersApi } from '../../../infrastructure/api/orders-api'
import {
  getEventColors,
  getEventIcon,
  getEntityTypeI18nKey,
  getActionI18nKey,
  extractMetadataDetail,
  getTimelineDescription,
  formatRelativeTime,
  formatAbsoluteTime,
} from './timeline-event.utils'

// ---------------------------------------------------------------------------
// Sub-components (dumb, no hooks)
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="px-6 py-4 space-y-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex gap-3 animate-pulse">
          <div className="w-7 h-7 rounded-full bg-muted flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-2 pt-1">
            <div className="flex gap-2">
              <div className="h-4 w-20 bg-muted rounded-full" />
              <div className="h-4 w-16 bg-muted rounded-full" />
            </div>
            <div className="h-3.5 w-3/4 bg-muted rounded" />
            <div className="h-3 w-1/2 bg-muted rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-4">
        <Activity className="w-5 h-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">{t('orders.timelineEmpty')}</p>
      <p className="text-xs text-muted-foreground max-w-[220px]">{t('orders.timelineEmptyDesc')}</p>
    </div>
  )
}

function TimelineEventRow({ event, isLast }: { event: OrderEvent; isLast: boolean }) {
  const { t, i18n } = useTranslation()
  const colors = getEventColors(event.action)
  const Icon = getEventIcon(event.action)
  const metaDetail = extractMetadataDetail(event, t as (key: string, options?: Record<string, unknown>) => string)
  const description = getTimelineDescription(event, t as (key: string, options?: Record<string, unknown>) => string)
  const entityLabel = t(getEntityTypeI18nKey(event.entityType), { defaultValue: event.entityType })
  const actionLabel = t(getActionI18nKey(event.action), { defaultValue: event.action.replace(/_/g, ' ') })

  return (
    <div className={cn('flex gap-3 relative', !isLast && 'pb-5')}>
      {/* Connector line */}
      {!isLast && (
        <div className="absolute left-3.5 top-7 bottom-0 w-px bg-border/60" />
      )}

      {/* Dot with icon */}
      <div className={cn('w-7 h-7 rounded-full border-2 flex-shrink-0 flex items-center justify-center z-10', colors.dot)}>
        <Icon className={cn('w-3.5 h-3.5', colors.icon)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        {/* Action badge + entity type + timestamp */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide', colors.badge)}>
            {actionLabel}
          </span>
          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
            {entityLabel}
          </span>
          <span
            className="text-[10px] text-muted-foreground ml-auto cursor-default"
            title={formatAbsoluteTime(event.createdAt, i18n.language)}
          >
            {formatRelativeTime(event.createdAt, i18n.language)}
          </span>
        </div>

        {/* Description */}
        {description && (
          <p className="text-sm text-foreground leading-snug">
            {description}
          </p>
        )}

        {/* Metadata detail — secondary info line */}
        {metaDetail && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{metaDetail}</p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface OrderTimelineTabProps {
  orderId: string
  entityTypes?: OrderEventEntityType[]
}

function getChangedFields(event: OrderEvent): string[] {
  const changedFields = event.metadata?.changedFields
  return Array.isArray(changedFields) ? changedFields.map(String) : []
}

function isOrderSystemUpdateEvent(event: OrderEvent): boolean {
  if (event.entityType !== 'ORDER' || event.action !== 'UPDATED') return false
  const changedFields = getChangedFields(event)
  return (
    changedFields.includes('sapSalesOrderNumber')
    || changedFields.includes('pricingSnapshot')
  )
}

function isShipmentTransitionEvent(event: OrderEvent): boolean {
  if (event.entityType !== 'SHIPMENT' || event.action !== 'STATUS_CHANGED') return false
  const previousStatus = String(event.metadata?.previousStatus ?? '')
  const newStatus = String(event.metadata?.newStatus ?? '')
  return (
    (previousStatus === 'pending' && newStatus === 'loading')
    || (previousStatus === 'loading' && newStatus === 'completed')
  )
}

function isDownloadQrPdfEvent(event: OrderEvent): boolean {
  const description = String(event.description ?? '')
  return description.toLowerCase().includes('download qr pdf')
}

function isSuperAdminOnlyEvent(event: OrderEvent): boolean {
  if (event.metadata?.supportAction === true) return true
  if (event.entityType === 'QUEUE_ENTRY') return true
  if (event.entityType === 'ORDER' && event.action === 'CREATED') return true
  if (event.entityType === 'SHIPMENT' && event.action === 'CREATED') return true
  if (isOrderSystemUpdateEvent(event)) return true
  if (isShipmentTransitionEvent(event)) return true
  if (isDownloadQrPdfEvent(event)) return true
  return false
}

function applyTimelineVisibilityRules(events: OrderEvent[], isSuperAdmin: boolean): OrderEvent[] {
  if (isSuperAdmin) return events
  return events.filter((event) => !isSuperAdminOnlyEvent(event))
}

export function OrderTimelineTab({ orderId, entityTypes }: OrderTimelineTabProps) {
  const [events, setEvents] = useState<OrderEvent[]>([])
  const [loading, setLoading] = useState(false)
  const roleType = useSelector(selectUserRoleType)
  const isSuperAdmin = roleType === 'super_admin'

  useEffect(() => {
    if (!orderId) return
    setLoading(true)
    ordersApi
      .getOrderTimeline(orderId)
      .then((all) => {
        const visibleEvents = applyTimelineVisibilityRules(all, isSuperAdmin)
        setEvents(
          entityTypes && entityTypes.length > 0
            ? visibleEvents.filter((e) => entityTypes.includes(e.entityType))
            : visibleEvents
        )
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false))
  }, [orderId, entityTypes, isSuperAdmin])

  if (loading) return <LoadingSkeleton />
  if (events.length === 0) return <EmptyState />

  return (
    <div className="px-6 py-4">
      {events.map((event, index) => (
        <TimelineEventRow
          key={event.id}
          event={event}
          isLast={index === events.length - 1}
        />
      ))}
    </div>
  )
}
